import { DocumentActions } from '@/components/DocumentActions'
import {
  Alert,
  Button,
  Card,
  CardContent,
  Chip,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { Dialog } from '@/components/AppDialog'
import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import EditIcon from '@mui/icons-material/Edit'
import { useState } from 'react'
import {
  useDeleteGatePassTemplate,
  useDispatchGatePass,
  useEquipmentItems,
  useGatePasses,
  useGatePassTemplates,
  useReturnGatePass,
} from '@/api/hooks'
import type { EquipmentItem, GatePass, GatePassLine, GatePassTemplate } from '@/api/types'
import { GatePassTemplateDialog } from '@/pages/gatepasses/GatePassTemplateDialog'
import { CATEGORIES, EquipmentItemDialog } from '@/pages/gatepasses/EquipmentItemDialog'
import { useAuthStore } from '@/auth/authStore'
import { GatePassDialog } from '@/pages/gatepasses/GatePassDialog'
import {
  EmptyState,
  ErrorBanner,
  formatDateTime,
  formatMoney,
  formatNumber,
  humanise,
  LoadingBlock,
  PageHeader,
  StatusChip,
} from '@/components/common'

type PassType = 'OUTDOOR_VESSEL' | 'HALL_ASSET'

export function GatePassesPage() {
  const can = useAuthStore((state) => state.can)
  const canWrite = useAuthStore((state) => state.canWrite)
  // The two registers and the store behind them. The catalogue sits here
  // because this is where it is felt: a vessel that is not marked as one is a
  // vessel nobody can put on an outpass.
  const [tab, setTab] = useState<PassType | 'ITEMS' | 'TEMPLATES'>('OUTDOOR_VESSEL')
  const type: PassType = tab === 'ITEMS' || tab === 'TEMPLATES' ? 'OUTDOOR_VESSEL' : tab
  const [addingTemplate, setAddingTemplate] = useState(false)
  const [editingTemplate, setEditingTemplate] = useState<GatePassTemplate | null>(null)
  const [templateRegister, setTemplateRegister] = useState<PassType>('OUTDOOR_VESSEL')
  const { data: passTemplates } = useGatePassTemplates(templateRegister)
  const retireTemplate = useDeleteGatePassTemplate()
  const [itemCategory, setItemCategory] = useState('VESSEL')
  const [addingItem, setAddingItem] = useState(false)
  const [editingItem, setEditingItem] = useState<EquipmentItem | null>(null)
  const { data: storeItems } = useEquipmentItems(itemCategory || undefined)
  const [status, setStatus] = useState('')
  const [editing, setEditing] = useState<GatePass | null>(null)
  const [createOpen, setCreateOpen] = useState(false)
  const [returning, setReturning] = useState<GatePass | null>(null)

  const { data, isLoading, error } = useGatePasses(type, { status: status || undefined })
  const dispatchPass = useDispatchGatePass()

  const passes = data?.content ?? []
  const vessels = type === 'OUTDOOR_VESSEL'

  return (
    <>
      <PageHeader
        title={vessels ? 'Vessel outpasses' : 'Hall asset handovers'}
        subtitle={
          vessels
            ? 'Vessels and equipment sent out for outdoor catering, and what came back'
            : 'Hall property handed to the guest, and what was collected again'
        }
        actions={
          <Stack direction="row" spacing={1}>
            <TextField
              select
              size="small"
              label="Status"
              value={status}
              onChange={(event) => setStatus(event.target.value)}
              sx={{ minWidth: 180 }}
            >
              <MenuItem value="">All</MenuItem>
              {['DRAFT', 'ISSUED', 'PARTIALLY_RETURNED', 'RETURNED', 'CANCELLED'].map((value) => (
                <MenuItem key={value} value={value}>
                  {value.replace(/_/g, ' ').toLowerCase()}
                </MenuItem>
              ))}
            </TextField>
            {tab !== 'ITEMS' && (can('equipment.issue') || canWrite('equipment')) ? (
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
                New {vessels ? 'outpass' : 'handover'}
              </Button>
            ) : null}
          </Stack>
        }
      />

      <Tabs
        value={tab}
        onChange={(_, value: PassType | 'ITEMS' | 'TEMPLATES') => setTab(value)}
        sx={{ mb: 2 }}
      >
        <Tab label="Outdoor vessels" value="OUTDOOR_VESSEL" />
        <Tab label="Hall assets" value="HALL_ASSET" />
        <Tab label="Store items" value="ITEMS" />
        <Tab label="Templates" value="TEMPLATES" />
      </Tabs>

      {tab === 'TEMPLATES' ? (
        <Card>
          <CardContent>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ mb: 2 }}
            >
              <TextField
                select
                size="small"
                label="Register"
                value={templateRegister}
                onChange={(event) => setTemplateRegister(event.target.value as PassType)}
                sx={{ minWidth: 220 }}
              >
                <MenuItem value="OUTDOOR_VESSEL">Vessel outpasses</MenuItem>
                <MenuItem value="HALL_ASSET">Hall handovers</MenuItem>
              </TextField>
              {canWrite('equipment') ? (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setAddingTemplate(true)}
                >
                  New template
                </Button>
              ) : null}
            </Stack>

            {!passTemplates || passTemplates.length === 0 ? (
              <EmptyState message="No template for this register yet. One saves adding the same items by hand on every pass." />
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Code</TableCell>
                    <TableCell>Template</TableCell>
                    <TableCell>For</TableCell>
                    <TableCell align="right">Items</TableCell>
                    <TableCell>What it is for</TableCell>
                    <TableCell align="right" />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {passTemplates.map((template: GatePassTemplate) => (
                    <TableRow key={template.id} hover>
                      <TableCell>{template.code}</TableCell>
                      <TableCell>{template.name}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          variant="outlined"
                          label={template.serviceType ? humanise(template.serviceType) : 'Any job'}
                        />
                      </TableCell>
                      <TableCell align="right">{template.lines.length}</TableCell>
                      <TableCell sx={{ maxWidth: 300 }}>{template.description ?? '—'}</TableCell>
                      <TableCell align="right">
                        {canWrite('equipment') ? (
                          <Tooltip title="Change this template">
                            <IconButton size="small" onClick={() => setEditingTemplate(template)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : null}
                        {/* Retired rather than deleted: passes raised from it
                            name things that are still out. */}
                        {can('equipment.delete') ? (
                          <Tooltip title="Retire this template">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => retireTemplate.mutate(template.id)}
                              disabled={retireTemplate.isPending}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      ) : null}

      {tab === 'ITEMS' ? (
        <Card>
          <CardContent>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ mb: 2 }}
            >
              <TextField
                select
                size="small"
                label="Category"
                value={itemCategory}
                onChange={(event) => setItemCategory(event.target.value)}
                sx={{ minWidth: 260 }}
              >
                <MenuItem value="">Everything the store holds</MenuItem>
                {CATEGORIES.map((option) => (
                  <MenuItem key={option.code} value={option.code}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
              {canWrite('equipment') ? (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setAddingItem(true)}
                >
                  Add item
                </Button>
              ) : null}
            </Stack>

            {!storeItems || storeItems.length === 0 ? (
              <EmptyState
                message={
                  itemCategory === 'VESSEL'
                    ? 'Nothing is marked as a vessel yet, so an outpass has nothing to offer. Add one, or move an existing item into Vessels.'
                    : 'Nothing in the store under this category.'
                }
              />
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Code</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell>Kind</TableCell>
                    <TableCell align="right">In stock</TableCell>
                    <TableCell align="right">Replacement</TableCell>
                    <TableCell align="right" />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {storeItems.map((storeItem: EquipmentItem) => (
                    <TableRow key={storeItem.id} hover>
                      <TableCell>{storeItem.code}</TableCell>
                      <TableCell>{storeItem.name}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          variant="outlined"
                          color={storeItem.category === 'VESSEL' ? 'primary' : 'default'}
                          label={humanise(storeItem.category ?? 'Uncategorised')}
                        />
                      </TableCell>
                      <TableCell>{humanise(storeItem.equipmentType)}</TableCell>
                      <TableCell align="right">
                        {formatNumber(storeItem.stockQuantity)} {storeItem.unit}
                      </TableCell>
                      <TableCell align="right">{formatMoney(storeItem.replacementCost)}</TableCell>
                      <TableCell align="right">
                        {canWrite('equipment') ? (
                          <Tooltip title="Change this item">
                            <IconButton size="small" onClick={() => setEditingItem(storeItem)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      ) : tab === 'TEMPLATES' ? null : (
      <>

      <ErrorBanner error={error} />

      <Card>
        <CardContent>
          {isLoading ? <LoadingBlock /> : null}
          {!isLoading && passes.length === 0 ? (
            <EmptyState
              message="Nothing here yet"
              hint={`Raise a ${vessels ? 'vessel outpass' : 'handover'} against a confirmed order.`}
            />
          ) : null}

          {passes.length > 0 ? (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Number</TableCell>
                  <TableCell>Order</TableCell>
                  <TableCell>{vessels ? 'Vehicle / driver' : 'Hall / guest'}</TableCell>
                  <TableCell>Issued</TableCell>
                  <TableCell>Due back</TableCell>
                  <TableCell align="right">Items out</TableCell>
                  <TableCell align="right">Pending</TableCell>
                  <TableCell align="right">Recovery</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {passes.map((pass) => (
                  <TableRow key={pass.id} hover>
                    <TableCell>{pass.issueNumber}</TableCell>
                    <TableCell>
                      {pass.orderNumber}
                      <Typography variant="caption" display="block" color="text.secondary">
                        {pass.customerName}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {vessels ? pass.vehicleNumber ?? '-' : pass.hallName ?? '-'}
                      <Typography variant="caption" display="block" color="text.secondary">
                        {vessels ? pass.driverName ?? '' : pass.issuedToName ?? ''}
                      </Typography>
                    </TableCell>
                    <TableCell>{formatDateTime(pass.issuedAt)}</TableCell>
                    <TableCell>{formatDateTime(pass.expectedReturnAt)}</TableCell>
                    <TableCell align="right">{formatNumber(pass.totalIssuedQuantity)}</TableCell>
                    <TableCell align="right">
                      <Typography
                        variant="body2"
                        color={pass.totalPendingQuantity > 0 ? 'warning.main' : 'text.secondary'}
                      >
                        {formatNumber(pass.totalPendingQuantity)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      {pass.recoveryWaived ? 'waived' : formatMoney(pass.totalRecoveryAmount)}
                    </TableCell>
                    <TableCell>
                      <StatusChip status={pass.status} />
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        {/* A draft is still ours; once it is out of the gate,
                            what left the store is a fact and only a return
                            moves the figures. */}
                        {pass.status === 'DRAFT' && can('equipment.issue') ? (
                          <Tooltip title="Correct this draft">
                            <IconButton size="small" onClick={() => setEditing(pass)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : null}
                        {pass.status === 'DRAFT' && can('equipment.issue') ? (
                          <Button
                            size="small"
                            onClick={() => dispatchPass.mutate({ id: pass.id })}
                            disabled={dispatchPass.isPending}
                          >
                            Dispatch
                          </Button>
                        ) : null}
                        {(pass.status === 'ISSUED' || pass.status === 'PARTIALLY_RETURNED') &&
                        can('equipment.issue') ? (
                          <Button size="small" onClick={() => setReturning(pass)}>
                            Receive back
                          </Button>
                        ) : null}
                        <DocumentActions
                          path={`/api/v1/gate-passes/${pass.id}/pdf`}
                          fileName={`${pass.issueNumber.replace(/\//g, '-')}.pdf`}
                          label="pass"
                        />
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : null}

          {dispatchPass.error ? <ErrorBanner error={dispatchPass.error} /> : null}
        </CardContent>
      </Card>

      </>
      )}

      <GatePassTemplateDialog
        open={addingTemplate}
        onClose={() => setAddingTemplate(false)}
      />
      <GatePassTemplateDialog
        open={editingTemplate !== null}
        template={editingTemplate}
        onClose={() => setEditingTemplate(null)}
      />

      <EquipmentItemDialog
        open={addingItem}
        defaultCategory={itemCategory || 'VESSEL'}
        onClose={() => setAddingItem(false)}
      />
      <EquipmentItemDialog
        open={editingItem !== null}
        item={editingItem}
        onClose={() => setEditingItem(null)}
      />

      <GatePassDialog
        open={createOpen}
        type={type}
        onClose={() => setCreateOpen(false)}
      />
      <GatePassDialog
        open={editing !== null}
        type={editing?.issueType ?? type}
        pass={editing}
        onClose={() => setEditing(null)}
      />
      <ReturnDialog pass={returning} onClose={() => setReturning(null)} />
    </>
  )
}

// ------------------------------------------------------------------- return

type ReturnEntry = { returned: string; damaged: string; missing: string; condition: string }

function ReturnDialog({ pass, onClose }: { pass: GatePass | null; onClose: () => void }) {
  const record = useReturnGatePass()
  const [entries, setEntries] = useState<Record<string, ReturnEntry>>({})

  const entryFor = (line: GatePassLine): ReturnEntry =>
    entries[line.id] ?? { returned: '', damaged: '', missing: '', condition: '' }

  const update = (lineId: string, patch: Partial<ReturnEntry>) =>
    setEntries((current) => ({
      ...current,
      [lineId]: { ...(current[lineId] ?? { returned: '', damaged: '', missing: '', condition: '' }), ...patch },
    }))

  if (!pass) return null

  const rows = pass.lines.filter((line) => line.pendingQuantity > 0)

  const submit = () => {
    const lines = pass.lines
      .map((line) => ({ line, entry: entryFor(line) }))
      .filter(({ entry }) => entry.returned || entry.damaged || entry.missing)
      .map(({ line, entry }) => ({
        lineId: line.id,
        returnedQuantity: Number(entry.returned || 0),
        damagedQuantity: Number(entry.damaged || 0),
        missingQuantity: Number(entry.missing || 0),
        conditionOnReturn: entry.condition || undefined,
      }))
    if (lines.length === 0) return
    record.mutate(
      { id: pass.id, body: { lines } },
      {
        onSuccess: () => {
          setEntries({})
          onClose()
        },
      },
    )
  }

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Receive back — {pass.issueNumber}</DialogTitle>
      <DialogContent>
        <ErrorBanner error={record.error} />
        <Alert severity="info" sx={{ mb: 2 }}>
          Record only what came back this time. Damage and shortage are charged at the rate on the pass;
          returned items go back into stock.
        </Alert>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Item</TableCell>
              <TableCell align="right">Still out</TableCell>
              <TableCell>Returned</TableCell>
              <TableCell>Damaged</TableCell>
              <TableCell>Missing</TableCell>
              <TableCell>Condition</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((line) => {
              const entry = entryFor(line)
              return (
                <TableRow key={line.id}>
                  <TableCell>
                    {line.itemName}
                    <Typography variant="caption" display="block" color="text.secondary">
                      {formatMoney(line.replacementRate)} per {line.unit ?? 'unit'} if not returned
                    </Typography>
                  </TableCell>
                  <TableCell align="right">{formatNumber(line.pendingQuantity)}</TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      type="number"
                      sx={{ width: 90 }}
                      value={entry.returned}
                      onChange={(event) => update(line.id, { returned: event.target.value })}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      type="number"
                      sx={{ width: 90 }}
                      value={entry.damaged}
                      onChange={(event) => update(line.id, { damaged: event.target.value })}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      type="number"
                      sx={{ width: 90 }}
                      value={entry.missing}
                      onChange={(event) => update(line.id, { missing: event.target.value })}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      value={entry.condition}
                      onChange={(event) => update(line.id, { condition: event.target.value })}
                    />
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
        {rows.length === 0 ? <EmptyState message="Everything on this pass is already accounted for" /> : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button variant="contained" onClick={submit} disabled={record.isPending || rows.length === 0}>
          Record return
        </Button>
      </DialogActions>
    </Dialog>
  )
}
